import React, { useEffect, useState } from 'react';
import { getImporters, createImporter, updateImporter, toggleImporterActive, uploadImporterLogo, uploadImporterSignature } from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Power, Loader2, Upload, Image, PenTool } from 'lucide-react';

export default function ImportersPage() {
  const { t } = useI18n();
  const [importers, setImporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', rfc: '', address: '', legal_rep_name: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => { loadImporters(); }, []);

  const loadImporters = async () => {
    try {
      const res = await getImporters();
      setImporters(res.data);
    } catch (err) {
      toast.error(t('entities.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t('common.required')); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateImporter(editing.id, form);
        toast.success(t('common.updatedOk'));
      } else {
        await createImporter(form);
        toast.success(t('common.createdOk'));
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', rfc: '', address: '', legal_rep_name: '' });
      loadImporters();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('entities.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (importer) => {
    setEditing(importer);
    setForm({
      name: importer.name,
      rfc: importer.rfc || '',
      address: importer.address || '',
      legal_rep_name: importer.legal_rep_name || '',
    });
    setShowForm(true);
  };

  const handleToggleActive = async (importerId) => {
    try {
      await toggleImporterActive(importerId);
      loadImporters();
      toast.success(t('entities.stateChanged'));
    } catch (err) {
      toast.error(t('entities.errorToggle'));
    }
  };

  const handleFileUpload = async (importerId, type, file) => {
    if (!file) return;
    setUploading(`${importerId}-${type}`);
    try {
      if (type === 'logo') {
        await uploadImporterLogo(importerId, file);
      } else {
        await uploadImporterSignature(importerId, file);
      }
      toast.success(t('common.updatedOk'));
      loadImporters();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('entities.errorSaving'));
    } finally {
      setUploading(null);
    }
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ name: '', rfc: '', address: '', legal_rep_name: '' });
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="importers-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl">{t('entities.importers')}</h1>
            <p className="text-muted-foreground">{importers.length} {t('entities.importers').toLowerCase()}</p>
          </div>
          <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-importer-btn">
                <Plus size={16} className="mr-2" />
                {t('entities.addImporter')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editing ? t('entities.editImporter') : t('entities.addImporter')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('common.name')} *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      placeholder="Nombre de la importadora"
                      className="mt-1"
                      data-testid="importer-name-input"
                    />
                  </div>
                  <div>
                    <Label>{t('entities.rfc')}</Label>
                    <Input
                      value={form.rfc}
                      onChange={(e) => setForm({...form, rfc: e.target.value.toUpperCase()})}
                      placeholder="RFC de la empresa"
                      className="mt-1 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label>{t('entities.address')}</Label>
                  <Textarea
                    value={form.address}
                    onChange={(e) => setForm({...form, address: e.target.value})}
                    placeholder="Direccion completa de la importadora"
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>{t('entities.legalRepName')}</Label>
                  <Input
                    value={form.legal_rep_name}
                    onChange={(e) => setForm({...form, legal_rep_name: e.target.value})}
                    placeholder="Nombre del representante legal"
                    className="mt-1"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
                  <Button type="submit" disabled={saving} data-testid="save-importer-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full dense-table">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                <th className="py-2 px-3 font-medium">{t('common.name')}</th>
                <th className="py-2 px-3 font-medium">{t('entities.rfc')}</th>
                <th className="py-2 px-3 font-medium">{t('entities.legalRepName')}</th>
                <th className="py-2 px-3 font-medium">{t('entities.docs')}</th>
                <th className="py-2 px-3 font-medium">{t('common.status')}</th>
                <th className="py-2 px-3 font-medium w-24">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">{t('common.loading')}</td></tr>
              ) : importers.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">{t('entities.noImporters')}</td></tr>
              ) : (
                importers.map((importer) => (
                  <tr key={importer.id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`importer-row-${importer.id}`}>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        {importer.logo_url ? (
                          <img src={importer.logo_url} alt="" className="w-6 h-6 object-contain rounded" />
                        ) : null}
                        <div>
                          <span className="font-medium">{importer.name}</span>
                          {importer.address && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">{importer.address}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-sm font-mono text-muted-foreground">{importer.rfc || '—'}</td>
                    <td className="py-2 px-3 text-sm text-muted-foreground">{importer.legal_rep_name || '—'}</td>
                    <td className="py-2 px-3">
                      <div className="flex gap-1">
                        <label className="cursor-pointer" title={t('entities.uploadLogo')}>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(importer.id, 'logo', e.target.files[0])}
                          />
                          <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border ${importer.logo_url ? 'border-emerald-500/30 text-emerald-400' : 'border-border text-muted-foreground'}`}>
                            {uploading === `${importer.id}-logo` ? <Loader2 size={10} className="animate-spin" /> : <Image size={10} />}
                            Logo
                          </span>
                        </label>
                        <label className="cursor-pointer" title={t('entities.uploadSignature')}>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(importer.id, 'signature', e.target.files[0])}
                          />
                          <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border ${importer.signature_url ? 'border-emerald-500/30 text-emerald-400' : 'border-border text-muted-foreground'}`}>
                            {uploading === `${importer.id}-signature` ? <Loader2 size={10} className="animate-spin" /> : <PenTool size={10} />}
                            {t('entities.signature')}
                          </span>
                        </label>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${importer.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                        {importer.is_active ? t('users.active') : t('users.inactive')}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(importer)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleToggleActive(importer.id)} className={importer.is_active ? 'text-amber-400' : 'text-emerald-400'}>
                          <Power size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
